import Vue from 'vue'
import Vuex, { Store, StoreOptions } from 'vuex'
import axios from '@/plugins/axios'
import {AuthState, BoarderState, FoodOrderingState, GlobalState, ReportingState} from '@/types/state'
import * as getterTypes from '@/store/getter-types'
import * as mutationTypes from '@/store/mutation-types'
import * as actionTypes from '@/store/action-types'
import authModule from '@/store/modules/auth'
import customerModule from '@/store/modules/customer'
import boarderModule from '@/store/modules/boarder'
import dailyMenuModule from '@/store/modules/dailymenu'
import reservationModule from '@/store/modules/reservation'
import reportingModule from '@/store/modules/reporting'
import foodDatabaseModule from '@/store/modules/food-database'
import foodOrderingModule from '@/store/modules/food-ordering'
import Tag from '@/model/tennant/Tag'
import {AlgorithmList} from "@/model/AlgorithmList";
import {
  AdminDeliverFood,
  BillingData,
  BoarderDetailResponse,
  NumberOfOrdersResponse,
  TagResponse
} from '@/types/service-response'
import {
  BankAccountPayload,
  BankTransactionListPayloadWithCancelToken,
  BankTransactionPayload,
  BankTransactionResolvePayload,
  DeleteCalendarEventPayload,
  FilterBillingsPayloadWithCancelToken,
  FetchBoarderDetailPayload,
  NewCalendarEventPayload,
  NumberOfOrdersWithCancelTokenPayload,
  PayloadTenantName,
  PayloadUpdateAddress,
  PayloadUpdateBank,
  PostalAddressPayload,
  TenantNamePayload,
  UpdateBoarderDetailPayload,
  UpdatePasswordPayload,
  FilterEventsWithCancelTokenPayload,
  UpdateTagPayload,
  NewTagPayload,
  AddNewBusinessPayload,
  CreateNewMessageTemplatePayload,
  UpdateMessageTemplatePayload,
  MessagePayload,
  UpdateBusinessPayload,
  NewTimeBlockPayload,
  UpdateTimeBlockPayload,
  SettingsPayload,
  NewSettingConfigurationPayload,
  ChangeNewPasswordPayload,
  UpdateAccountEmail,
  UserPayload,
  MoveBoarderToGroupPayload
} from "@/types/payload";
import {
  CREATE_NEW_SMART_LINK,
  DELETE_SELECTED_CONFIGURATION, DELETE_SELECTED_SMART_LINK,
  FETCH_ACTION_KIND_LIST,
  FETCH_REQUEST_KIND_LIST,
  FETCH_SMARTLINKS,
  CREATE_CALENDAR_EVENT_FOR_DAY, DELETE_KEY,
  DELETE_SELECTED_TAG, FETCH_CUSTOMER_BY_ID, FETCH_CUSTOMER_DATA, FETCH_SETTINGS_KEY,
  FETCH_TAG_BY_ID,
  SAVE_NEW_TAG,
  SAVE_NEW_TIME_BLOCK, SEARCH_SMART_LINK, SET_SMART_LINK_FILTER,
  UPDATE_BUSINESS,
  UPDATE_NEW_CONFIGURATION_SETTINGS,
  UPDATE_SELECTED_SMART_LINK,
  UPDATE_TAG, FETCH_BOARDER_QRCODE
} from "@/store/action-types";
import moment from "moment";
import PostalAddress from "@/model/tennant/PostalAddress";
import BankAccount from "@/model/tennant/BankAccount";
import TenantName from "@/model/tennant/TenantName";
import CalendarEventForDay from "@/model/calendar/CalendarEventForDay";
import {CalendarEvent} from "vuetify";
import CalendarEventClass from "@/model/calendar/CalendarEvent";
import {CreditDetailsPayload} from "@/types/payload";
import CreditDetails from "@/model/CreditDetails";
import BankTransaction from "@/model/BankTransaction/BankTransaction";
import BankTransactionImport from "@/model/BankTransactionImport";
import {Invoice} from "@/model/billing/Invoice";
import {Debt} from "@/model/debtors/Debt";
import MenuGroupList from "@/model/menu/MenuGroupList";
import BusinessModel from "@/model/menu/BusinessModel";
import ApiKey from "@/model/ApiKey";
import {MessageTemplateList} from "@/model/MessageTemplateList";
import BusinessTimeBlockCollection from "@/model/business/BusinessTimeBlockCollection";
import KeyList from "@/model/KeyList";
import SettingsKeyList from "@/model/SettingsKeyList";
import {AxiosResponse} from "axios";
import {CurrentUser} from "@/model/CurrentUser";
import {AccountEmail} from "@/model/AccountEmail";
import i18n from "@/i18n";
import vuetify from "@/plugins/vuetify";
import Language from "@/model/Language";
import {CURRENT_LANGUAGE_FIELD} from "@/constants";
import Request from "@/model/Request";
import LatestReservation from "@/model/LatestReservation";
import {BoarderGroupResponse} from "@/model/boarder/BoarderGroup";
import {SET_SMART_LINK_FILTER_MUTATION} from "@/store/mutation-types";
import {GET_SMART_LINK_FILTER} from "@/store/getter-types";
import RequestKindList from "@/model/RequestKindList";

Vue.use(Vuex)

const supportedLanguages: Language[] = [
  Language.create('sk', i18n.t('languages.slovakLanguageLabel').toString(), false),
  //Language.create('en', i18n.t('languages.englishLanguageLabel').toString(), false),
  Language.create('cs', i18n.t('languages.czechLanguageLabel').toString(), false),

]

const state = (): GlobalState => ({
  auth: {} as AuthState,
  foodOrdering: {} as FoodOrderingState,
  reporting: {} as ReportingState,
  boarder: {} as BoarderState,
  showAppDrawer: undefined,
  showErrorResponseDialog: false,
  errorResponseDialogTitle: undefined,
  errorResponseDialogResponse: undefined,
  errorResponseDialogInfoText: undefined,
  dataChanged: false,
  showProfileDialog: false,
  events: [],
  loadingWeekCalendar: false,
  supportedLanguages,
  currentUiLanguage: supportedLanguages[0],
  smartLinkFilter: [],
  keyList: [],
})

const storeOptions: StoreOptions<any> = {
  modules: {
    auth: authModule,
    customer: customerModule,
    boarder: boarderModule,
    dailyMenu: dailyMenuModule,
    reservation: reservationModule,
    reporting: reportingModule,
    foodDatabase: foodDatabaseModule,
    foodOrdering: foodOrderingModule,
  },

  state,

  getters: {
    [getterTypes.GET_SMART_LINK_FILTER] (state: GlobalState): Language[] {
      return state.smartLinkFilter
    },

    [getterTypes.SUPPORTED_LANGUAGES] (state: GlobalState): Language[] {
      return state.supportedLanguages
    },

    [getterTypes.CURRENT_UI_LANGUAGE] (state: GlobalState): Language {
      return state.currentUiLanguage
    },

    [getterTypes.IS_USER_LOGGED_IN] (state: GlobalState): boolean {
      return !!state.auth.currentUser
    },

    [getterTypes.APP_DRAWER_STATE] (state: GlobalState): boolean {
      return !!state.showAppDrawer
    },

    [getterTypes.GET_KEY_LIST] (state: GlobalState): KeyList[] {
      return state.keyList
    },

    [getterTypes.IS_DATA_CHANGED] (state: GlobalState): boolean {
      return state.dataChanged
    },

    [getterTypes.GET_CALENDAR_EVENTS] (state: GlobalState): CalendarEventForDay[] {
      return state.events
    },

    [getterTypes.GET_LOADING_WEEKCALENDAR] (state: GlobalState): boolean {
      return state.loadingWeekCalendar
    },

    [getterTypes.SHOW_ERROR_RESPONSE_DIALOG] (state: GlobalState): boolean {
      return state.showErrorResponseDialog
    },

    [getterTypes.ERROR_RESPONSE_DIALOG_DATA] (state: GlobalState): any {
      return {
        title: state.errorResponseDialogTitle,
        errorResponse: state.errorResponseDialogResponse,
        infoText: state.errorResponseDialogInfoText
      }
    },

    [getterTypes.GET_CONFIG] (state: GlobalState): any {
      return {
        topGroupIsExclusive: true,
        tagConfig: [{
          tagId:1,
          selection:'single',
          mandatory: true,
          title: 'polievka'
        },
          {
            tagId:5,
            selection:'single',
            mandatory: true,
            title: 'hlavné jedlo'
          },
          {
            tagId:8,
            selection:'single',
            mandatory: true,
            title: "bageta"
          },
          {
            tagId:11,
            selection:'single',
            mandatory: true,
            title: "minútka"
          },

          {//omacka nepovinna
            tagId:6,
            selection:'single',
            mandatory: false,
            title: "omáčka"
          },

          {
            tagId:11,
            selection:'single',
            mandatory: true,
            title: "šalát"
          },


        ]
      }
    }

  },

  mutations: {
    [mutationTypes.SET_SMART_LINK_FILTER_MUTATION] (state: GlobalState, payload: any[]) {
      state.smartLinkFilter = payload
    },

    [mutationTypes.SET_CURRENT_UI_LANGUAGE] (state: GlobalState, payload: Language): void {
      state.currentUiLanguage = payload
      localStorage.setItem(CURRENT_LANGUAGE_FIELD, payload.code)
    },

    [mutationTypes.TOGGLE_APP_DRAWER_MUTATION] (state: GlobalState): void {
      state.showAppDrawer = !state.showAppDrawer
    },

    [mutationTypes.SET_DRAWER_STATE_MUTATION] (state: GlobalState, payload: boolean): void {
      state.showAppDrawer = payload
    },

    [mutationTypes.SET_DATA_CHANGED] (state: GlobalState, payload: boolean): void {
      state.dataChanged = payload
    },

    [mutationTypes.SET_ERROR_RESPONSE_DIALOG_DATA] (state: GlobalState, payload: any): void {
      state.showErrorResponseDialog = payload.show
      state.errorResponseDialogTitle = payload.title
      state.errorResponseDialogResponse = payload.errorResponse
      state.errorResponseDialogInfoText = payload.infoText
    },

    [mutationTypes.SET_CALENDAR_EVENTS] (state: GlobalState, payload: CalendarEventForDay[]) {
      state.events = payload
    },
    [mutationTypes.SET_LOADING_WEEKCALENDAR](state: GlobalState, payload: boolean): void {
      state.loadingWeekCalendar = payload
    },
  },

  actions: {
    [actionTypes.SWITCH_APPLICATION_LANGUAGE] ({ commit }: { commit: Function }, payload: Language): void {
      vuetify.framework.lang.current = payload.code
      vuetify.framework.rtl = payload.rtl

      i18n.locale = payload.code

      if (this.getters[getterTypes.CURRENT_UI_LANGUAGE] !== payload) {
        commit(mutationTypes.SET_CURRENT_UI_LANGUAGE, payload)
      }
    },

    [actionTypes.TOGGLE_APP_DRAWER] ({ commit }: { commit: Function }): void {
      commit(mutationTypes.TOGGLE_APP_DRAWER_MUTATION)
    },

    [actionTypes.SET_SMART_LINK_FILTER] ({ commit }: { commit: Function }, payload: any): void {
      commit(mutationTypes.SET_SMART_LINK_FILTER_MUTATION,payload)
    },

    [actionTypes.SET_DRAWER_STATE] ({ commit }: { commit: Function }, payload: boolean): void {
      commit(mutationTypes.SET_DRAWER_STATE_MUTATION, payload)
    },

    [actionTypes.SHOW_RESPONSE_ERROR_DIALOG] ({ commit }: { commit: Function }, payload: any): void {
      commit(mutationTypes.SET_ERROR_RESPONSE_DIALOG_DATA, payload)
    },

    [actionTypes.HIDE_RESPONSE_ERROR_DIALOG] ({ commit }: { commit: Function }): void {
      commit(mutationTypes.SET_ERROR_RESPONSE_DIALOG_DATA, {
        show: false,
        title: undefined,
        errorResponse: undefined
      })
    },

    async [actionTypes.FETCH_TAGS] ({ rootState }: { rootState: GlobalState }): Promise<Tag[]> {
      const tenantCode: string = rootState.auth.currentUser!.tenant.code

      const response = await axios.get(`/api/tag`)

      return Tag.createFromResponse(response.data as TagResponse)
    },

    async [actionTypes.SAVE_NEW_TAG]({  }, payload: NewTagPayload): Promise<any> {
      const response = await axios.post('/api/tag', {
        title: payload.title,
        iconClass: payload.iconClass
      })
      return response.data
    },

    async [actionTypes.FETCH_TAG_BY_ID] ({ commit }: { commit: Function}, payload: number): Promise<Tag> {
      const response = await axios.get(`/api/tag/${payload}`)

      return Tag.createFromModel(response.data)
    },

    async [actionTypes.UPDATE_TAG] ({ commit }: { commit: Function}, payload: UpdateTagPayload): Promise<void> {
      await axios.put(`/api/tag/${payload.id}`,
          {
            title: payload.title,
            iconClass: payload.iconClass
          })
    },

    async [actionTypes.DELETE_SELECTED_TAG] ({  }, payload: string): Promise<void> {

      const response = await axios.delete(`api/tag/${payload}`)

      return response.data

    },



    async [actionTypes.FETCH_PERSON_CREDENTIAL_BY_ID] ({ commit }: { commit: Function}, payload: string): Promise<string> {
      const response = await axios.get(`/api/person-credentials/${payload}`)

      return response.data.loginName
    },

    async [actionTypes.UPDATE_PERSON_CREDENTIAL_PASS_BY_ID] ({ commit }: { commit: Function}, payload: UpdatePasswordPayload): Promise<void> {
      await axios.put(`/api/person-credentials/${payload.personId}`,
          {
            plainTextPassword: payload.password,
            loginName: payload.loginName,
            sendNotification: payload.sendNotification,
            forcePasswordChange:payload.forcePasswordChange
          })
    },

    async [actionTypes.FETCH_ALGORITHM_LIST] ({ commit }: { commit: Function}): Promise<AlgorithmList[]> {
      const response = await axios.get(`/api/price-algorithm`)

      return AlgorithmList.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_POSTAL_ADDRESS] ({  }): Promise<PostalAddress> {

      const response = await axios.get('/api/postal-address')

      return response.data
    },

    async [actionTypes.FETCH_BANK_ACCOUNT] ({  }): Promise<BankAccount> {
      const response = await axios.get('/api/bank-account')

      return response.data
    },

    async [actionTypes.FETCH_TENANT_NAME] ({  }): Promise<TenantName> {
      const response = await axios.get('/api/tenant-settings')

      return response.data
    },

    async [actionTypes.CREATE_POSTAL_ADDRESS]({  }, payload: PostalAddressPayload): Promise<PostalAddress> {
      const response = await axios.post('/api/postal-address', payload)

      return PostalAddress.createPostalAddress(response.data)
    },

    async [actionTypes.CREATE_BANK_ACCOUNT]({  }, payload: BankAccountPayload): Promise<BankAccount> {
      const response = await axios.post('/api/bank-account', payload)

      return BankAccount.createBankAccount(response.data)
    },

    async [actionTypes.CREATE_TENANT_NAME] ({  }, payload: TenantNamePayload): Promise<TenantName> {
      const response = await axios.post('/api/tenant-settings', payload)

      return TenantName.createTenantName(response.data)
    },

    async [actionTypes.UPDATE_POSTAL_ADDRESS]({  }, payload: PayloadUpdateAddress): Promise<PostalAddress> {

      let data = {
        street: payload.changedData.street,
        city: payload.changedData.city,
        zip: payload.changedData.zip
      }

      const response = await axios.put(`/api/postal-address/${payload.id}`, data)

      return PostalAddress.createPostalAddress(response.data)
    },

    async [actionTypes.UPDATE_BANK_ACCOUNT]({  }, payload: PayloadUpdateBank): Promise<BankAccount> {

      let data = {
        iban: payload.changedData.iban
      }

      const response = await axios.put(`/api/bank-account/${payload.id}`, data)

      return BankAccount.createBankAccount(response.data)
    },

    async [actionTypes.UPDATE_TENANT_NAME] ({  }, payload: PayloadTenantName): Promise<TenantName> {

      let data = {
        value: payload.changedData.value
      }

      const response = await axios.put(`/api/tenant-settings/${payload.id}`, data)

      return TenantName.createTenantName(response.data)
    },

    async [actionTypes.FETCH_CALENDAR_EVENTS_LIST] ({  }): Promise<CalendarEvent[]> {

      const response = await axios.get('/api/calendar/event-kind')

      return CalendarEventClass.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_CALENDAR_EVENTS_FOR_DAY] ({ commit }: { commit: Function }, payload: FilterEventsWithCancelTokenPayload): Promise<CalendarEventForDay[]> {

      commit(mutationTypes.SET_LOADING_WEEKCALENDAR, true)

      const response = await axios.get(`/api/calendar/global-event`, {
        cancelToken: payload.cancelToken?.token,
        params: {
            "date[after]": moment(payload.payload.after).format('YYYY-MM-DD'),
            "date[before]": moment(payload.payload.before).format('YYYY-MM-DD')
        }})

      const calendarEvents: CalendarEventForDay[] = CalendarEventForDay.createCalendarEvent(response.data)

      commit(mutationTypes.SET_CALENDAR_EVENTS, calendarEvents)

      commit(mutationTypes.SET_LOADING_WEEKCALENDAR, false)

      return calendarEvents
    },

    async [actionTypes.FETCH_NUMBER_OF_ORDERS] ({  }, payload: NumberOfOrdersWithCancelTokenPayload): Promise<NumberOfOrdersResponse> {

      const response = await axios.post('api/statistics/reservation-status', payload.payload, {
        cancelToken: payload.cancelToken?.token
      } )

      return response.data
    },

    async [CREATE_CALENDAR_EVENT_FOR_DAY] ({  }, payload: NewCalendarEventPayload): Promise<CalendarEventForDay> {

      const response = await axios.post('api/calendar/global-event', payload)

      return CalendarEventForDay.createCalendarEventForDay(response.data)
    },

    async [actionTypes.DELETE_CALENDAR_EVENT_FOR_DAY] ({  }, payload: DeleteCalendarEventPayload): Promise<void> {

      const response = await axios.delete(`api/calendar/global-event/${payload.id}`)

      return response.data

    },

    async [actionTypes.INSERT_CREDIT_OPERATION] ({  }, payload: CreditDetailsPayload): Promise<CreditDetails> {
      const response = await axios.post('/api/wallet-entry', payload)

      return CreditDetails.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_CREDIT_HISTORY] ({  }, payload: CreditDetailsPayload): Promise<CreditDetails[]> {
      const response = await axios.get(`/api/person/${payload}/wallet-entry`)

      return CreditDetails.createFromResponseList(response.data)
    },

    async [actionTypes.INIT_STORE]({commit, dispatch}: { commit: Function, dispatch: Function }): Promise<void> {

      await Promise.all([
        dispatch(actionTypes.AUTH_INIT_MODULE),
      ])
    },

    async [actionTypes.ADMIN_DELIVER_FOOD] ({ }, id: number): Promise<AdminDeliverFood> {
      const response = await axios.put(`/api/food-ordering/offer-reservation-admin-delivery/${id}`)

      return response.data
    },

    async [actionTypes.FETCH_BILLINGS] ({ }, payload: FilterBillingsPayloadWithCancelToken): Promise<Invoice[]> {
      let requestParams = {}

      if (payload.payload.after && payload.payload.before && !payload.payload.unpaid) {
        requestParams = {
          periodStart: moment(payload.payload.after).format('YYYY-MM-DD'),
          periodEnd: moment(payload.payload.before).format('YYYY-MM-DD'),
        }
      } else if (payload.payload.unpaid && !payload.payload.after && !payload.payload.before) {
        requestParams = {
          "unpaid": payload.payload.unpaid
        }
      } else {
        requestParams = {
          periodStart: moment(payload.payload.after).format('YYYY-MM-DD'),
          periodEnd: moment(payload.payload.before).format('YYYY-MM-DD'),
            //"unpaid": payload.payload.unpaid
        }
      }

      const response = await axios.get('/api/billing/invoice', {
        cancelToken: payload.cancelToken?.token,
        params: {
          periodStart: moment(payload.payload.after).format('YYYY-MM-DD'),
          periodEnd: moment(payload.payload.before).format('YYYY-MM-DD'),
        }})

      return Invoice.createListFromResponse(response.data)
    },

    async [actionTypes.CREATE_NEW_BILLING] ({ }): Promise<BillingData[]> {

      const response = await axios.put('')

      return response.data
    },

    async [actionTypes.DELETE_BILLING] ({ }): Promise<BillingData[]> {

      const response = await axios.delete('')

      return response.data
    },

    async [actionTypes.FETCH_BANK_TRANSACTION_LIST] ({  }, payload: BankTransactionListPayloadWithCancelToken ): Promise<BankTransaction[]> {
      const response = await axios.get(`/api/billing/transaction`, {
        cancelToken: payload.cancelToken ?.token,
        params: {
          "date[before]": payload.payload.dateBefore ? moment(payload.payload.dateBefore).format('YYYY-MM-DD') : null,
          "date[after]": payload.payload.dateAfter ? moment(payload.payload.dateAfter).format('YYYY-MM-DD') : null,
          "status": payload.payload.status
        }
      })

      return BankTransaction.createFromResponseList(response.data)
    },

    async [actionTypes.CREATE_NEW_TRANSACTION] ({  }, payload:BankTransactionPayload ): Promise<BankTransaction> {
      const response = await axios.post('/api/billing/transaction', payload)

      return BankTransaction.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_BANK_TRANSACTION_DETAIL] ({  }, payload: number): Promise<BankTransaction> {
      const response = await axios.get(`/api/billing/transaction/${payload}`,)

      return BankTransaction.createFromResponse(response.data)
    },

    async [actionTypes.UPLOAD_TRANSACTION] ({  }, payload: string ): Promise<BankTransactionImport> {
      const response = await axios.post('/api/billing/transaction/import', {
        "fileContent": payload
      })
      return BankTransactionImport.createFromResponse(response.data)
    },

    async [actionTypes.RESOLVE_TRANSACTION] ({ }, payload: BankTransactionResolvePayload): Promise<BankTransaction> {
      const response = await axios.put(`/api/billing/transaction/${payload.id}`,
          {
            'variableSymbol': payload.variableSymbol,
            'specificSymbol': payload.specificSymbol,
            'status': payload.status
          })
      return BankTransaction.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_BOARDER_DETAIL] ({}, payload: FetchBoarderDetailPayload): Promise<BoarderDetailResponse> {
      const response = await axios.get(`/api/person/${payload.id}`)

      return response.data
    },

    async [actionTypes.FETCH_BOARDER_DETAIL_WITH_CANCEL] ({}, payload: any): Promise<BoarderDetailResponse> {
      const response = await axios.get(`/api/person/${payload.data}`, {
        cancelToken: payload.cancelToken?.token
      })

      return response.data
    },

    async [actionTypes.UPDATE_BOARDER_DETAIL] ({}, payload: UpdateBoarderDetailPayload): Promise<BoarderDetailResponse> {
      const response = await axios.put(`/api/person/${payload.id}`, payload)

      return response.data
    },

    // person settings staff may manage for a boarder (allowlisted server-side)
    async [actionTypes.FETCH_BOARDER_SETTINGS] ({ rootState }: { rootState: GlobalState }, payload: { boarderId: number }): Promise<Array<{ key: string, type: string, value: string | null }>> {
      const tenantCode: string = rootState.auth.currentUser!.tenant.code
      const response = await axios.get(`/api/${tenantCode}/food-ordering/boarder/${payload.boarderId}/settings`)

      return response.data
    },

    async [actionTypes.UPDATE_BOARDER_SETTINGS] ({ rootState }: { rootState: GlobalState }, payload: { boarderId: number, settings: Array<{ key: string, type: string, value: string }> }): Promise<Array<{ key: string, type: string, value: string | null }>> {
      const tenantCode: string = rootState.auth.currentUser!.tenant.code
      const response = await axios.put(`/api/${tenantCode}/food-ordering/boarder/${payload.boarderId}/settings`, payload.settings)

      return response.data
    },

    async [actionTypes.FETCH_INVOICE] ({  }, payload: any): Promise<Invoice> {
      const response = await axios.get(`/api/invoice/${payload}`,)

      return Invoice.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_DEBTORS](): Promise<Debt[]> {

      const response = await axios.get('/api/debtor')

      return Debt.createListFromResponse(response.data)
    },

    async [actionTypes.FETCH_DEBTOR_DETAIL]({ }, id: number): Promise<Debt> {
      const response = await axios.get(`/api/debtor/${id}`)

      return Debt.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_BUSINESS_DATA] ({  }): Promise<BusinessModel[]> {
      const response = await axios.get('/api/business')

      return response.data
    },

    async [actionTypes.SAVE_NEW_GROUP] ({ },payload: any): Promise<any> {
      const response = await axios.post(`/api/menu-group`, payload)

      return response.data
    },

    async [actionTypes.FETCH_MENU_GROUP_DATA] ({  }, businessId: string): Promise<MenuGroupList[]> {
      const response = await axios.get(`/api/menu-group?businessId=${businessId}`)

      return MenuGroupList.createFromResponseList(response.data)
    },

    async [actionTypes.SAVE_UPDATED_GROUP] ({}, payload: any): Promise<any> {
      const response = await axios.put(`/api/menu-group/${payload.id}`,payload.data)

      return response.data
    },
    async [actionTypes.CREATE_NEW_SLOT] ({ },payload: any): Promise<any> {
      const response = await axios.post(`/api/menu-group-slot`, payload)

      return response.data
    },

    async [actionTypes.UPDATE_SLOT_ORDER] ({}, payload: any): Promise<any> {
      const response = await axios.put(`/api/menu-group-slot/${payload.id}`, {
        order: payload.order
      })

      return response.data
    },

    async [actionTypes.SAVE_NEW_KEY] ({}): Promise<void> {
      const response = await axios.post('/api/api-key',{})

      return response.data
    },

    async [actionTypes.GET_API_KEY] ({  }): Promise<ApiKey[]> {
      const response = await axios.get(`/api/api-key`)

      return ApiKey.createFromResponseList(response.data)
    },

    async [actionTypes.DELETE_KEY] ({  }, payload: string): Promise<void> {

      const response = await axios.delete(`/api/api-key/${payload}`)

      return response.data

    },

    async [actionTypes.SAVE_NEW_BUSINESS] ({}, payload: AddNewBusinessPayload): Promise<BusinessModel> {
      const response = await axios.post('/api/business', payload)

      return BusinessModel.createFromResponse(response.data)
    },

    async [actionTypes.UPDATE_BUSINESS] ({}, payload: UpdateBusinessPayload): Promise<BusinessModel> {
      const response = await axios.put(`/api/business/${payload.id}`, {
          name: payload.name
      })

      return BusinessModel.createFromResponse(response.data)
    },

    async [actionTypes.SAVE_NEW_MESSAGE_TEMPLATE] ({  }, payload: CreateNewMessageTemplatePayload ): Promise<MessageTemplateList> {
      const response = await axios.post('/api/message/template', {
        "name": payload.name,
        "subject":payload.subject,
        "text":payload.text
      })
      return response.data
    },

    async [actionTypes.FETCH_MESSAGE_TEMPLATE_LIST] ({ }: { commit: Function }): Promise<MessageTemplateList[]> {

      const response = await axios.get('/api/message-template')

      return MessageTemplateList.createFromResponseList(response.data)
    },

    async [actionTypes.UPDATE_MESSAGE_TEMPLATE] ({}, payload: UpdateMessageTemplatePayload): Promise<void> {
      await axios.put(`/api/message/template/${payload.id}`, payload)
    },

    async [actionTypes.DELETE_MESSAGE_TEMPLATE] ({  }, payload: any): Promise<void> {
      await axios.delete(`api/message/template/${payload}`)
    },

    async [actionTypes.SEND_NEW_MESSAGE] ({  }, payload: MessagePayload ): Promise<void> {
      await axios.post('/api/message/send', {
        "addressees": payload.addressees,
        "subject": payload.subject,
        "text":payload.text,
        "replyToAddress": payload.replyToAddress
      })
    },

    async [actionTypes.FETCH_TIME_BLOCK_COLLECTION] ({  },payload): Promise<BusinessTimeBlockCollection[]> {
      const response = await axios.get(`/api/time-block`, {
        params: {
          'business.id': payload
        }
      })

      return BusinessTimeBlockCollection.createFromResponseList(response.data)
    },

    async [actionTypes.SAVE_NEW_TIME_BLOCK] ({}, payload: NewTimeBlockPayload): Promise<any> {
      const response = await axios.post('/api/time-block', payload)

      return response.data
    },

    async [actionTypes.DELETE_NEW_TIME_BLOCK] ({  }, payload: string): Promise<void> {

      const response = await axios.delete(`/api/time-block/${payload}`)

      return response.data

    },

    async [actionTypes.UPDATE_TIME_BLOCK] ({}, payload: UpdateTimeBlockPayload): Promise<any> {
      const response = await axios.put(`/api/time-block/${payload.id}`, {
        title: payload.title,
        business:payload.business,
        order:payload.order,
        reservationDeadline: payload.reservationDeadline ? payload.reservationDeadline : null,
        deadlineForPreviousDay: payload.deadlineForPreviousDay
      })

      return BusinessModel.createFromResponse(response.data)
    },

    async [actionTypes.GET_KEY] ({state }: { state: GlobalState }): Promise<KeyList[]> {
      const response = await axios.get(`/api/tenant-settings`)

      state.keyList = KeyList.createFromResponseList(response.data)
      return KeyList.createFromResponseList(response.data)
    },

    async [actionTypes.SAVE_NEW_SETTING] ({  }, payload: SettingsPayload ): Promise<void> {
      await axios.put(`/api/tenant-settings/${payload.id}`, {
        "value": payload.value.toString(),
      })
    },

    async [actionTypes.SAVE_SETTINGS] ({},payload: any): Promise<void> {
      const response = await axios.post('/api/tenant-settings',{
        key: payload.key,
        value: payload.value.toString()
      })

      return response.data
    },

    async [actionTypes.GET_SETTING_DATA] ({  }): Promise<SettingsKeyList[]> {
      const response = await axios.get(`/api/settings-key`)

      return SettingsKeyList.createFromResponseList(response.data)
    },

    async [actionTypes.FETCH_KEY_BY_ID] ({  }): Promise<KeyList[]> {
      const response = await axios.get(`/api/tenant-settings`)

      return response.data
    },

    async [actionTypes.CREATE_NEW_CONFIGURATION_SETTINGS] ({  }, payload: NewSettingConfigurationPayload): Promise<any> {

      const response = await axios.post('api/configuration-group', {
        title: payload.title,
        settings: payload.settings,
        order: payload.order,
        business: payload.business,
        tags: payload.tags
      }
      )
      return response.data
    },

    async [actionTypes.UPDATE_NEW_CONFIGURATION_SETTINGS] ({  }, payload: any ): Promise<any> {
      console.debug(payload)

      const response = await axios.put(`/api/configuration-group/${payload.id}`, {
            title: payload.title,
            settings: payload.settings,
            order: payload.order,
            business: payload.business,
            tags: payload.tags
          }
      )

      return response.data
    },

    async [actionTypes.DELETE_SELECTED_CONFIGURATION] ({  }, payload: string): Promise<void> {

      const response = await axios.delete(`/api/configuration-group/${payload}`)

      return response.data

    },

    async [actionTypes.UPDATE_PERSON_CREDENTIAL_PASS] ({ commit }: { commit: Function}, payload: ChangeNewPasswordPayload): Promise<void> {
      await axios.put(`/api/account/password`,
          {
            "password": payload.password,
          })
    },

    async [actionTypes.FETCH_ACCOUNT_USER]({commit}: { commit: Function }): Promise<void> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.get('/api/account', {headers: headers})
      const user = CurrentUser.createFromUserInformationResponse({
        id: response.data.boarderList.id,
        givenName: response.data.firstName,
        familyName: response.data.familyName,
        isBoarder: response.data.isBoarder,
        isStaff: response.data.isStaff,
        staffRoleList: response.data.staffRoleList,
        boarderList: response.data.boarderList,
        tenant: response.data.tenant,
        phoneNumbers: response.data.phoneNumbers,
        loginName: response.data.loginName
      })

      commit(mutationTypes.AUTH_SET_CURRENT_USER_MUTATION, user)
    },

    async [actionTypes.UPDATE_ACCOUNT_EMAILS]({}, payload: UpdateAccountEmail[]): Promise<AccountEmail[]> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.put('/api/account/emails', payload, {headers: headers})
      return response.data
    },

    async [actionTypes.UPDATE_ACCOUNT_USER]({commit}: { commit: Function }, payload: UserPayload): Promise<void> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      await axios.put('/api/account', payload, {headers: headers})
    },

    async [actionTypes.FETCH_ACCOUNT_EMAILS]({}): Promise<AccountEmail[]> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }

      const response: AxiosResponse = await axios.get('/api/account/emails', {headers: headers})
      return AccountEmail.createFromResponse(response.data)
    },

    async [actionTypes.FETCH_REQUESTLIST] ({  },payload: any): Promise<Request[]> {
      const response = await axios.get(`/api/request`,{
        params: {
          'status': payload.toString()
        }

      })

      return Request.createFromResponseList(response.data)
    },

    async [actionTypes.ACCEPTED_REQUEST] ({},payload: any): Promise<any> {
      const response = await axios.put(`/api/request/${payload.id}/status`,{
        'status': payload.status,
        'dataOverride': payload.dataOverride
      })

      return response.data
    },

    async [actionTypes.FETCH_LATEST_RESERVATIONS] ({  },payload: any): Promise<LatestReservation[]> {
      const response = await axios.get(`/api/food-ordering/latest-offer-reservation?${payload.data}`,{
        cancelToken: payload.cancelToken?.token
      })

      return LatestReservation.createFromResponseList(response.data)
    },

    async [actionTypes.FETCH_CUSTOMER_BY_ID] ({},payload: any): Promise<any[]> {
      const response = await axios.get(`/api/customer/${payload}`)

      return response.data
    },

    async [actionTypes.FETCH_CUSTOMER_DATA] ({  }): Promise<any[]> {
      const response = await axios.get('/api/customer')

      return response.data
    },

    async [actionTypes.FETCH_STATISTICS] ({},payload: any): Promise<any[]> {
      const response = await axios.post(`/api/statistics/menu-group-summary`,{
        customer: payload.customer,
        dateFrom: payload.dateFrom,
        dateTo: payload.dateTo
      })

      return response.data
    },

    async [actionTypes.FETCH_SMARTLINKS] ({  }): Promise<any[]> {
      const response = await axios.get('/api/smart-link')

      return response.data
    },

    // landing_page_config rides the tenant detail; an empty config arrives
    // as [] (PHP array serialization) and must be treated as {}
    async [actionTypes.FETCH_LANDING_PAGE_CONFIG] ({ rootState }): Promise<Record<string, any>> {
      const tenantId = rootState.auth.currentUser!.tenant.id
      const response = await axios.get(`/api/tenant/${tenantId}`)
      const config = response.data.landingPageConfig

      return Array.isArray(config) ? {} : (config ?? {})
    },

    async [actionTypes.UPDATE_LANDING_PAGE_CONFIG] ({ rootState }, payload: Record<string, any>): Promise<void> {
      const tenantId = rootState.auth.currentUser!.tenant.id
      await axios.put(`/api/tenant/${tenantId}/landing-page-config`, payload)
    },

    async [actionTypes.FETCH_REQUEST_KIND_LIST] ({  }): Promise<RequestKindList[]> {
      const response = await axios.get('/api/request-kind')

      return RequestKindList.createFromResponseList(response.data)
    },

    async [actionTypes.FETCH_ACTION_KIND_LIST] ({  }): Promise<any[]> {
      const response = await axios.get('/api/smart-link-action-kind')

      return response.data
    },

    async [actionTypes.UPDATE_SELECTED_SMART_LINK] ({},payload: any): Promise<any> {
      const response = await axios.put(`/api/smart-link/${payload.id}`,{
        'activeFrom': payload.activeFrom,
        'activeUntil': payload.activeUntil,
        'configuration': payload.configuration
      })

      return response.data
    },

    async [actionTypes.CREATE_NEW_SMART_LINK]({rootState}: { rootState: GlobalState }, payload: any): Promise<void> {
      const response = await axios.post(`/api/smart-link`, payload,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
          })

      return response.data
    },

    async [actionTypes.SEARCH_SMART_LINK] ({  },payload: any): Promise<void> {
      const response = await axios.get('/api/smart-link',
          {
            params: {
              "slug": payload.slug,
              "actionKind.name":payload.actionKind,
              "requestKind.name": payload.requestKinds,
            }
          })

      return response.data
    },

    async [actionTypes.DELETE_SELECTED_SMART_LINK] ({  }, payload: string): Promise<void> {

      const response = await axios.delete(`/api/smart-link/${payload}`)

      return response.data

    },

    async [actionTypes.PRINT_SELECTED_SMART_LINK] ({  }, payload: string): Promise<any> {

      const response = await axios.post('/api/smart-link/flyer', {"smartLink": payload}, {
        responseType: 'blob',
      })

      return response
    },

    async [actionTypes.FETCH_BOARDER_QRCODE] ({  }, payload: any): Promise<any> {
      let headers = {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }
      const response = await axios.get('/api/boarder-identification-code',{
        params: {
            'boarder.id': payload
        }
      } )

      return response.data
    },
  }
}

export default new Store(storeOptions)
